import { HttpResponse } from '@angular/common/http';
import { delay, of } from 'rxjs';

import { handleAiProcessRequest } from './handlers/ai-process.handler';
import { handleAiUsageRequest } from './handlers/ai-usage.handler';
import { handleCasesRequest } from './handlers/cases.handler';
import { handleDocumentTypesRequest } from './handlers/document-types.handler';
import { handleEvidenceFilesRequest } from './handlers/evidence-files.handler';
import { handleExtractionSourcesRequest } from './handlers/extraction-sources.handler';
import { handleGroupsRequest } from './handlers/groups.handler';
import { handleMeRequest } from './handlers/me.handler';
import { handlePoliciesRequest } from './handlers/policies.handler';
import { handleProjectsRequest } from './handlers/projects.handler';
import { handleRequiredDocumentsRequest } from './handlers/required-documents.handler';
import { handleUsersRequest } from './handlers/users.handler';
import { environment } from '../../environments/environment';

import type { HttpInterceptorFn } from '@angular/common/http';
import type { Observable } from 'rxjs';

/**
 * Mockインターセプター
 * environment.useMock が true の場合のみAPIリクエストをインターセプトしてMockデータを返却
 */
export const mockInterceptor: HttpInterceptorFn = (req, next) => {
  // Mockが無効な場合はそのまま通過
  if (!environment.useMock) {
    return next(req);
  }

  // APIリクエスト以外はそのまま通過
  if (!req.url.includes('/api/')) {
    return next(req);
  }

  // HttpClient の params は req.url ではなく req.urlWithParams にシリアライズされる
  // mock 側はクエリパラメータを参照するため必ず urlWithParams を渡す
  const mockResponse = handleMockRequest(
    req.method,
    req.urlWithParams,
    req.body,
  );

  if (mockResponse) {
    // Mockレスポンスを返却（100-300msのランダム遅延を追加）
    const delayMs = Math.floor(Math.random() * 200) + 100;
    return mockResponse.pipe(delay(delayMs));
  }

  // Mockが見つからない場合はそのまま通過
  return next(req);
};

/**
 * テナントスコープのパスプレフィックス
 * /api/v0/tenants/{tenantId} にマッチ（tenantId をキャプチャ）
 */
const TENANT_PREFIX = /^\/api\/v0\/tenants\/([^/]+)/;

/**
 * Mockリクエストをハンドラーにルーティング
 */
function handleMockRequest(
  method: string,
  url: string,
  _body: unknown,
): Observable<HttpResponse<unknown>> | null {
  // URLからパスとクエリパラメータを抽出
  const urlObj = new URL(url, 'http://localhost');
  const path = urlObj.pathname;
  const searchParams = urlObj.searchParams;

  // /api/v0/me へのリクエスト（テナントスコープ外）
  if (path === '/api/v0/me') {
    return handleMeRequest(method, path);
  }

  // テナントスコープのパスからテナントプレフィックスを除去してルーティング
  const tenantMatch = TENANT_PREFIX.exec(path);
  if (!tenantMatch) {
    return null;
  }
  const tenantId = tenantMatch[1];
  const tenantPath = path.slice(tenantMatch[0].length);

  // 各ハンドラーにはテナントプレフィックス除去済みの短縮パスを渡す
  // （例: /groups, /groups/:id, /projects/:id/ai-process 等）

  // /ai-usage* へのリクエスト（Issue #475 Phase 1: AI 利用量スナップショット）
  if (tenantPath.startsWith('/ai-usage')) {
    return handleAiUsageRequest(method, tenantPath, tenantId);
  }

  // /groups* へのリクエスト
  if (tenantPath.startsWith('/groups')) {
    return handleGroupsRequest(
      method,
      tenantPath,
      _body,
      tenantId,
      searchParams,
    );
  }

  // /users* へのリクエスト
  if (tenantPath.startsWith('/users')) {
    return handleUsersRequest(method, tenantPath, _body);
  }

  // /policies* へのリクエスト
  if (tenantPath.startsWith('/policies')) {
    return handlePoliciesRequest(method, tenantPath, _body);
  }

  // /projects* へのリクエスト
  if (tenantPath.startsWith('/projects')) {
    // AI process ハンドラを最初にチェック（/projects/:id/ai-process/... を処理）
    // tenantId は AI 利用量の上限超過（422 UsageLimitExceeded）判定に使う
    const aiProcessResult = handleAiProcessRequest(
      method,
      tenantPath,
      _body,
      tenantId,
    );
    if (aiProcessResult) {
      return aiProcessResult;
    }
    // required-documents へのリクエストをチェック
    const requiredDocumentsResult = handleRequiredDocumentsRequest(
      method,
      tenantPath,
      searchParams,
    );
    if (requiredDocumentsResult) {
      return requiredDocumentsResult;
    }
    // document-types へのリクエストをチェック (Issue #447)
    const documentTypesResult = handleDocumentTypesRequest(
      method,
      tenantPath,
      searchParams,
    );
    if (documentTypesResult) {
      return documentTypesResult;
    }
    // evidence-files へのリクエストをチェック
    // Issue #453: 一覧 GET は sortBy / sortOrder / groupBy / search / cursor / limit を
    // 解釈するため searchParams を渡す
    const evidenceFilesResult = handleEvidenceFilesRequest(
      method,
      tenantPath,
      _body,
      searchParams,
    );
    if (evidenceFilesResult) {
      return evidenceFilesResult;
    }
    // extraction-sources へのリクエストをチェック
    const extractionSourcesResult = handleExtractionSourcesRequest(
      method,
      tenantPath,
      _body,
      searchParams,
    );
    if (extractionSourcesResult) {
      return extractionSourcesResult;
    }
    // cases へのリクエストをチェック
    const casesResult = handleCasesRequest(method, tenantPath, _body);
    if (casesResult) {
      return casesResult;
    }
    // プロジェクト詳細・メタデータ更新をチェック
    return handleProjectsRequest(method, tenantPath, _body);
  }

  return null;
}

/**
 * 成功レスポンスを生成するヘルパー関数
 */
export function createMockResponse<T>(
  body: T,
  status = 200,
): Observable<HttpResponse<T>> {
  return of(
    new HttpResponse({
      status,
      body,
    }),
  );
}

/**
 * エラーレスポンスを生成するヘルパー関数
 */
export function createMockErrorResponse(
  message: string,
  status = 400,
): Observable<HttpResponse<{ message: string }>> {
  return of(
    new HttpResponse({
      status,
      body: { message },
    }),
  );
}
